import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styles from '../SelectFeature.module.scss';
import ContactDetails from 'client/pages/MapPage/components/MapMenu/ContactDetails/ContactDetails';
import Tabs from 'client/components/GDS/Tabs/Tabs';
import TabPanel from 'client/components/GDS/Tabs/TabPanel/TabPanel';
import InfoBox from 'client/pages/MapPage/components/InfoBox/InfoBox';
import { useSelector } from 'react-redux';
import { useScrollToAnchor } from './useScrollToAnchor';
import { useSupplementaryData } from 'client/utils/hooks/useSupplementaryData';
import { getContactSections } from 'client/utils/helpers/SupplementaryData/SupplementaryDataUtils';
import {
    getAssetContacts,
    getAssetOwnerContactsByDataProviderId,
    getAssetOwnerRecordByDataProviderId
} from 'client/utils/helpers/AssetOwner/AssetContactUtils';
import ownerDetailsStyles from '../SelectFeaturePanel/ContactDetailsTab/ContactDetailsTab.module.scss';

/**
 * Lite version of SelectFeaturePanel for redacted assets
 * Shows only contact details since redacted assets don't have feature objects
 *
 * To open this panel (e.g., from InfoBox component in branch 14518), dispatch:
 * setSelectedRedactedAsset(assetData) where assetData contains:
 * - dataproviderassigneduniqueid: string (required)
 * - nuarActor: { label: string, displayName: string }
 * - contacts: { business: array, emergency: array }
 *
 * @example
 * import { setSelectedRedactedAsset } from 'client/store/mapSlice/mapSlice';
 * dispatch(setSelectedRedactedAsset({
 *   dataproviderassigneduniqueid: 'asset-123',
 *   nuarActor: { label: 'Asset Owner Name' },
 *   contacts: { business: [...], emergency: [...] }
 * }));
 *
 * TODO: Remove when US 13968 (full feature panel support for redacted assets) is implemented
 */
const RedactedAssetPanel = ({ assetData }) => {
    const panelRef = useRef();
    const contactsAnchorRef = useRef();
    const intl = useIntl();
    const supplementaryData = useSelector(state => state.supplementaryData.data);
    const assetOwnersData = useSelector(state => state?.assetOwners?.data);
    const contactDetailsTabLabel = intl.formatMessage({ id: 'selectFeaturePanel.tab.contactDetails' });
    const [activeTab, setActiveTab] = useState(contactDetailsTabLabel);

    const currentAssetId = assetData?.dataproviderassigneduniqueid;
    const assetIdsToFetch = useMemo(() => currentAssetId ? [currentAssetId] : [], [currentAssetId]);
    // hasError reflects this asset's own fetch failure — the global supplementaryData.error flag
    // is only ever set by the bulk enhanced measures retry path, not by per-feature fetches
    const { loading: supplementaryDataLoading, hasError: supplementaryDataError, fetch: fetchSuppData } = useSupplementaryData(assetIdsToFetch);

    useEffect(() => {
        if (currentAssetId && !supplementaryData?.[currentAssetId]) {
            fetchSuppData();
        }
    }, [currentAssetId]);

    useEffect(() => {
        if (!panelRef.current) {
            return;
        }

        // Reset scroll position to top when assetData changes
        if (typeof panelRef.current.scrollTo === 'function') {
            panelRef.current.scrollTo({ top: 0, behavior: 'auto' });
        } else {
            panelRef.current.scrollTop = 0;
        }
    }, [assetData]);

    const scrollContactsIntoView = useScrollToAnchor({
        containerRef: panelRef,
        targetRef: contactsAnchorRef,
        offset: -5
    });

    if (!assetData?.dataproviderassigneduniqueid) {
        return null;
    }

    const scrollToContacts = () => {
        setActiveTab(contactDetailsTabLabel);
        scrollContactsIntoView();
    };

    const asset = supplementaryData?.[assetData.dataproviderassigneduniqueid];
    const { nuarActor, contacts } = asset || { nuarActor: null, contacts: null };
    const dataProviderId = asset?.dataProviderId || assetData?.dataProviderId || assetData?.dataproviderid_fk;
    const assetOwnerRecord = getAssetOwnerRecordByDataProviderId({
        assetOwnersData,
        dataProviderId
    });
    const assetOwnerName = nuarActor?.label || nuarActor?.displayName || assetOwnerRecord?.label;
    const assetContacts = getAssetContacts({
        assetSource: asset,
        includeGenericContactsKey: true
    });
    const ownerContacts = getAssetOwnerContactsByDataProviderId({
        assetOwnersData,
        dataProviderId
    });

    const infoBoxAsset = {
        id: assetData.dataproviderassigneduniqueid,
        dataproviderassigneduniqueid: assetData.dataproviderassigneduniqueid,
        feature: null,
        nuarActor,
        contacts
    };

    const renderAssetOwnerName = () => {
        if (supplementaryDataError) {
            return intl.formatMessage({ id: 'redactedAssetPanel.assetOwnerError' });
        }

        if (assetOwnerName) {
            return assetOwnerName;
        }

        if (supplementaryDataLoading) {
            return intl.formatMessage({ id: 'redactedAssetPanel.assetOwnerLoading' });
        }

        return intl.formatMessage({ id: 'redactedAssetPanel.assetOwnerLoading' });
    };

    const contactSections = getContactSections({
        supplementaryDataEntries: asset ? [asset] : [],
        assetOwnerName,
        ownerContacts,
        assetContacts,
        supplementaryContactsSource: 'asset'
    });

    const contactDetailsFormatted = contactSections.map((section, index) => {
        const sectionHeading = section.heading || intl.formatMessage({ id: `contactDetailsTab.${section.sourceType}Heading` });

        return (
            <section
                key={[section.sourceType, sectionHeading || index, index].join('-')}
                className={section.sourceType === 'asset' ? ownerDetailsStyles.assetSection : ''}
                data-testid={`redactedAssetPanel.section.${section.sourceType}.${index}`}
            >
                {sectionHeading
                    ? <h4 className="govuk-heading-s" data-testid={`redactedAssetPanel.sectionHeading.${section.sourceType}.${index}`}>
                        {sectionHeading}
                    </h4>
                    : null}
                {section.contacts.business?.length
                    ? <ContactDetails
                        contacts={section.contacts.business}
                        caption={intl.formatMessage({ id: 'contactDetails.businessContact' })}
                        type='business'
                        variant="simple"
                        data-testid={`redactedAssetPanel.${section.sourceType}.business.${index}`}
                    />
                    : null}
                {section.contacts.emergency?.length
                    ? <ContactDetails
                        contacts={section.contacts.emergency}
                        caption={intl.formatMessage({ id: 'contactDetails.emergencyContact' })}
                        type='emergency'
                        variant="simple"
                        data-testid={`redactedAssetPanel.${section.sourceType}.emergency.${index}`}
                    />
                    : null}
            </section>
        );
    });

    const hasContactDetails = contactDetailsFormatted.length > 0;
    return <div aria-live='polite' className={[styles.panelContent, 'govuk-width-container'].join(' ')} ref={panelRef}>
        <div className={styles.content}>
            <div className={styles.assetOwnerSection} data-testid='redactedAssetOwnerSection'>
                <div className={['govuk-body-l govuk-!-font-weight-bold', styles.assetOwnerName, 'govuk-!-margin-bottom-0'].join(' ')} data-testid='redactedAssetOwnerName'>
                    {renderAssetOwnerName()}
                </div>
            </div>
        </div>
        <InfoBox
            variant={assetData.infoBoxVariant}
            assetOwner={assetOwnerName}
            action={scrollToContacts}
            asset={infoBoxAsset}
            data-testid="redactedAssetPanel.infoBox"
        />
        {hasContactDetails && (
            <div ref={contactsAnchorRef}>
                <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
                    <TabPanel data-testid="redactedContactDetailsTab" label={contactDetailsTabLabel}>
                        <div>
                            {contactDetailsFormatted}
                        </div>
                    </TabPanel>
                </Tabs>
            </div>
        )}
    </div>;
};

RedactedAssetPanel.propTypes = {
    assetData: PropTypes.shape({
        dataproviderassigneduniqueid: PropTypes.string.isRequired,
        dataproviderid_fk: PropTypes.string,
        dataProviderId: PropTypes.string,
        infoBoxVariant: PropTypes.oneOf(['permissionRequired', 'specialGuidance', 'proceedWithCaution', 'furtherConsultation'])
    })
};

export default RedactedAssetPanel;
